import React, { useState, useRef, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  TextField
} from '@mui/material';

// Sample questions - you can modify these
const questions = [
  "What is the purpose of your visit to the USA?",
  "How long do you plan to stay in the USA?",
  "Where will you stay during your visit?",
  "Who will be traveling with you?",
  "Why is your father not traveling with you?",
  "What places do you plan to visit in the USA?",
  "Who is funding your trip?",
  "How much have you saved for this trip?",
  "Do you own any property or assets in India?",
  "What does your father do?",
  "What does your mother do?",
  "Have you booked your flight tickets?",
  "What will you do if your visa gets rejected?",
  "Have you applied for a US visa before?",
  "Do you have any relatives in the USA?",
  "What does your elder brother do?",
  "What will you do after returning to India?",
  "Do you plan to extend your stay in the USA?",
  "Where do you currently work?",
  "What is your annual salary?",
  "Have you traveled internationally before?",
  "Do you plan to settle in the USA?",
  "Does you parents has visa?",
  "Why is your parents traveling with you?",
  "Do you intend to immigrate to the U.S.?",
  "When you are planning to travel?",
  "Has an immigration petition been filed for you?",
  "Why don’t you wait for your immigration petition to be approved?",
  "Has anyone in your family been denied a U.S. visa?", 
  "Why was your visa denied earlier?",
  "Your parents have a B1/B2 visa. Have they traveled to the U.S.?",
  "Will your parents stay in the US longer than you?",
  "Have you ever been denied a US Visa?",
  "What do you do as a GIS Manager?",
  "Do you any other relatives other than your brother in USA?",
  "Will you work in US?",
  "Have your parents visited US before?",
  "What status your elder brother is on?",
  "What your younder brother do?",
  "Why are you visiting?",
  "What do you studied?",
  "What is your toal work experience?",
  "How long you are working with this company?",
  "What visa does your brother is having?",
  "How long is your brother staying is USA for?",
  "When are you planning to travel?",
  "Why are you planning during this period?",
  "What is your age?",
  "Are you married?"
];

function App() {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [qaLogs, setQaLogs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [remainingQuestions, setRemainingQuestions] = useState([]);
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const speechSynthesisRef = useRef(null);
  const recognitionRef = useRef(null);
  const [recordingUrl, setRecordingUrl] = useState(null);

  const speakQuestion = (question) => {
    // Cancel any ongoing speech
    if (speechSynthesisRef.current) {
      window.speechSynthesis.cancel();
    }

    // Create new speech utterance
    const utterance = new SpeechSynthesisUtterance(question);
    utterance.rate = 0.9; // Slightly slower rate for better clarity
    utterance.pitch = 1;
    utterance.volume = 1.0; // Maximum volume
    speechSynthesisRef.current = utterance;

    // Speak the question
    window.speechSynthesis.speak(utterance);
  };

  const startInterview = async () => {
    // Create a copy of questions and shuffle them
    const shuffledQuestions = [...questions].sort(() => Math.random() - 0.5);
    setRemainingQuestions(shuffledQuestions);
    setCurrentQuestion(shuffledQuestions[0]);
    setQaLogs([]);
    setTranscript('');
    
    // Start recording first
    await startRecording();
    
    // Wait a moment for the recording to initialize
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Then speak the first question
    speakQuestion(shuffledQuestions[0]);
  };

  const getNextQuestion = async () => {
    const currentIndex = remainingQuestions.indexOf(currentQuestion);
    // Add current question and answer to logs before moving to next
    setQaLogs(prevLogs => [...prevLogs, { 
      question: currentQuestion, 
      answer: transcript,
      timestamp: new Date().toISOString() 
    }]);
    setTranscript(''); // Clear transcript for next question

    if (currentIndex < remainingQuestions.length - 1) {
      const nextQuestion = remainingQuestions[currentIndex + 1];
      setCurrentQuestion(nextQuestion);
      
      // Wait a moment for the state to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Speak the next question
      speakQuestion(nextQuestion);
    } else {
      setCurrentQuestion(null);
      // Don't stop recording here, let the user do it manually
    }
  };

  const startRecording = async () => {
    try {
      // Get microphone input for both recording and speech recognition
      const micStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });

      // Create a new MediaRecorder with microphone input
      const mediaRecorder = new MediaRecorder(micStream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      // Start speech recognition with the same microphone stream
      if ('webkitSpeechRecognition' in window) {
        const recognition = new window.webkitSpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1;

        let currentTranscript = '';

        recognition.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Update the transcript
          setTranscript(prev => {
            // If we have a final transcript, use it as the base
            if (finalTranscript.trim()) {
              currentTranscript = finalTranscript.trim();
            }
            // Add interim transcript if it exists
            return interimTranscript ? `${currentTranscript} ${interimTranscript}` : currentTranscript;
          });
        };

        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
        };

        recognition.onend = () => {
          // Restart recognition if we're still recording
          if (isRecording) {
            try {
              recognition.start();
            } catch (error) {
              console.error('Error restarting recognition:', error);
            }
          }
        };

        recognitionRef.current = recognition;
        recognition.start();
      }

      // Start media recording
      mediaRecorder.start(1000);
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing audio devices:', error);
      alert('Please allow access to your microphone to record the interview properly.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Stop speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      return new Promise((resolve) => {
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
          const url = URL.createObjectURL(blob);
          setRecordingUrl(url);
          chunksRef.current = [];
          resolve(url);
        };
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      });
    }
    return Promise.resolve(null);
  };

  const downloadRecordings = async () => {
    setIsProcessing(true);
    try {
      // Create a timestamp for the files
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      // First handle the recording if it's still active
      let finalRecordingUrl = recordingUrl;
      if (isRecording) {
        finalRecordingUrl = await stopRecording();
      }

      // Download transcript
      const transcriptData = {
        date: new Date().toISOString(),
        questions: qaLogs.map((log, index) => ({
          questionNumber: index + 1,
          question: log.question,
          answer: log.answer,
          timestamp: log.timestamp
        }))
      };

      const transcriptBlob = new Blob([JSON.stringify(transcriptData, null, 2)], { 
        type: 'application/json' 
      });
      const transcriptUrl = URL.createObjectURL(transcriptBlob);
      const transcriptLink = document.createElement('a');
      transcriptLink.href = transcriptUrl;
      transcriptLink.download = `interview-transcript-${timestamp}.json`;
      document.body.appendChild(transcriptLink);
      transcriptLink.click();
      document.body.removeChild(transcriptLink);
      URL.revokeObjectURL(transcriptUrl);

      // Then download the recording if we have it
      if (finalRecordingUrl) {
        const audioLink = document.createElement('a');
        audioLink.href = finalRecordingUrl;
        audioLink.download = `interview-recording-${timestamp}.webm`;
        document.body.appendChild(audioLink);
        audioLink.click();
        document.body.removeChild(audioLink);
        URL.revokeObjectURL(finalRecordingUrl);
        setRecordingUrl(null);
      }
    } catch (error) {
      console.error('Error downloading recordings:', error);
      alert('Error downloading files. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (speechSynthesisRef.current) {
        window.speechSynthesis.cancel();
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Interview Practice App
        </Typography>

        {!currentQuestion ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={startInterview}
              size="large"
            >
              Start Interview
            </Button>
          </Box>
        ) : (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Question {remainingQuestions.indexOf(currentQuestion) + 1} of {remainingQuestions.length}:
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.2rem' }}>
              {currentQuestion}
            </Typography>
            {isRecording && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Live Transcript:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={transcript}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={getNextQuestion}
                size="large"
              >
                Next Question
              </Button>
            </Box>
          </Paper>
        )}

        {qaLogs.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Interview Progress
            </Typography>
            <List>
              {qaLogs.map((log, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={`Question ${index + 1}`}
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            Q: {log.question}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2" color="text.secondary">
                            A: {log.answer}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            {!currentQuestion && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={downloadRecordings}
                  disabled={isProcessing}
                  startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isProcessing ? "Processing..." : "Download Interview Recording & Transcript"}
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
}

export default App;
